import { useEffect, useState, useCallback } from "react";
import { api, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { GitBranch, Camera, RotateCcw, Trash2, GitCompare, Loader2, Layers } from "lucide-react";

const Delta = ({ value, suffix = "" }) => {
  if (value === 0) return <span className="text-slate-500">±0{suffix}</span>;
  return (
    <span className={value > 0 ? "text-emerald-400" : "text-rose-400"}>
      {value > 0 ? "+" : ""}{fmtNum(value)}{suffix}
    </span>
  );
};

export default function VersionVault({ crawlId, onChanged }) {
  const [versions, setVersions] = useState([]);
  const [deep, setDeep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(null);

  const load = useCallback(async () => {
    if (!crawlId) return;
    try {
      const d = await api.listVersions(crawlId);
      setVersions(d.versions);
    } catch (e) { /* noop */ }
  }, [crawlId]);

  useEffect(() => { load(); setCompare(null); }, [load]);

  const snapshot = async () => {
    setBusy(true);
    try {
      const v = await api.createVersion(crawlId, deep);
      toast.success(`Snapshot v${v.version_number} saved${deep ? " (deep copy)" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not snapshot");
    } finally { setBusy(false); }
  };

  const doCompare = async (v) => {
    if (compare?.version?.id === v.id) { setCompare(null); return; }
    try {
      setCompare(await api.compareVersion(crawlId, v.id));
    } catch (e) { toast.error("Compare failed"); }
  };

  const rollback = async (v) => {
    setBusy(true);
    try {
      const r = await api.rollbackVersion(crawlId, v.id);
      toast.warning(`Rolled back to v${v.version_number} — ${r.pages} pages`);
      setCompare(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    } finally { setBusy(false); }
  };

  const remove = async (v) => {
    await api.deleteVersion(crawlId, v.id);
    toast.success(`Deleted v${v.version_number}`);
    if (compare?.version?.id === v.id) setCompare(null);
    load();
  };

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden" data-testid="version-vault">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border flex-wrap">
        <GitBranch className="h-4 w-4 text-amber-400" />
        <h3 className="font-display font-bold text-base">Dataset Versions</h3>
        <span className="font-mono text-[11px] text-slate-600">{versions.length} snapshots</span>
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch data-testid="version-deep-switch" checked={deep} onCheckedChange={setDeep} />
            <Label className="text-[11px] font-mono text-slate-400 cursor-pointer">deep copy</Label>
          </label>
          <Button size="sm" data-testid="version-snapshot-button" onClick={snapshot} disabled={busy}
            className="h-8 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            <span className="ml-1.5">Snapshot now</span>
          </Button>
        </div>
      </div>

      <div className="divide-y divide-border" data-testid="version-list">
        {versions.length === 0 && (
          <p className="px-5 py-6 text-xs font-mono text-slate-600">
            No snapshots yet. One is taken automatically before every "Improve this corpus" run — or take one now.
            Metadata snapshots are tiny; deep copies store the full pages so rollback is exact.
          </p>
        )}
        {versions.map((v) => (
          <div key={v.id} className="px-5 py-3" data-testid={`version-row-${v.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono font-bold text-sm text-slate-200">v{v.version_number}</span>
              <span className={cn("px-1.5 py-0.5 rounded font-mono text-[9px] uppercase border",
                v.deep ? "border-purple-500/40 bg-purple-500/10 text-purple-300"
                       : "border-slate-600 bg-slate-700/20 text-slate-400")}>
                {v.deep ? "deep" : "metadata"}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-600">{v.reason}</span>
              <span className="font-mono text-[11px] text-slate-500">{v.created_at?.slice(0, 16).replace("T", " ")}</span>
              <span className="font-mono text-[11px] text-slate-400 tabular-nums">
                {fmtNum(v.pages)} pages · {fmtNum(v.tokens)} tok · score {v.score}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="ghost" data-testid={`version-compare-${v.id}`} onClick={() => doCompare(v)}
                  className="h-7 text-slate-400 hover:text-cyan-300">
                  <GitCompare className="h-3.5 w-3.5 mr-1" /> <span className="font-mono text-[11px]">Compare</span>
                </Button>
                <Button size="sm" variant="ghost" data-testid={`version-rollback-${v.id}`} onClick={() => rollback(v)}
                  disabled={busy} className="h-7 text-slate-400 hover:text-amber-300">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> <span className="font-mono text-[11px]">Rollback</span>
                </Button>
                <Button size="sm" variant="ghost" data-testid={`version-delete-${v.id}`} onClick={() => remove(v)}
                  className="h-7 text-slate-500 hover:text-rose-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {compare?.version?.id === v.id && (
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="version-compare-result">
                {[
                  ["Pages", <Delta key="p" value={compare.delta.pages} />, `${fmtNum(compare.version.pages)} → ${fmtNum(compare.current.pages)}`],
                  ["Tokens", <Delta key="t" value={compare.delta.tokens} />, `${fmtNum(compare.version.tokens)} → ${fmtNum(compare.current.tokens)}`],
                  ["Score", <Delta key="s" value={compare.delta.score} />, `${compare.version.grade} → ${compare.current.grade}`],
                  ["Churn", <span key="c" className="text-slate-300">+{fmtNum(compare.added_pages)} / -{fmtNum(compare.removed_pages)}</span>, "added / removed"],
                ].map(([label, val, sub]) => (
                  <div key={label} className="rounded-md border border-border bg-[#0E1420] px-3 py-2">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-slate-600 flex items-center gap-1">
                      <Layers className="h-3 w-3" />{label}
                    </p>
                    <p className="font-mono text-sm mt-0.5 tabular-nums">{val}</p>
                    <p className="text-[10px] font-mono text-slate-600">{sub}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
